/**
 * Waterfall / Gantt view of the audit trail (timelines.corp.amazon.com style).
 *
 * One row per task, positioned on a shared time axis spanning the evaluation.
 * Bars are colored by pipeline stage; when the backend stamped an execution
 * duration, the queued/blocked prefix of the bar renders translucent and the
 * actual execution window renders solid. Renders nothing for legacy trails
 * that carry no per-task timing.
 */

import { useMemo, useState } from 'react';
import { Box, SpaceBetween } from '@cloudscape-design/components';
import type { AuditEntry } from '@amzn/helianthus-arbiter-client';
import {
  buildGanttModel,
  formatTickLabel,
  isRuleEvalTask,
  summarizeRuleEvals,
  toPercent,
  type GanttTask,
} from '../utils/auditTrailGantt';
import { STAGE_LABELS, STAGE_ORDER, type Stage } from '../utils/auditTrailStages';
import { formatDuration } from '../utils/auditTrailMetrics';

/** Stage color palette (Cloudscape data-visualization-ish hues). */
const STAGE_COLORS: Record<Stage, string> = {
  INTAKE: '#8456ce',
  INITIALIZATION: '#0972d3',
  // Teal, deliberately distinct from the outcome pass green: rule bars are
  // colored by outcome, so this stage color only marks coordination tasks.
  RULE_EVALUATION: '#0d7d8c',
  POST_PROCESSOR: '#d97706',
  FINALIZATION: '#5f6b7a',
};

/** Rule outcome colors: pass green, fail red, everything else amber. */
// Width of the legend's group-label column so swatch rows align.
const LEGEND_LABEL_WIDTH = 110;

const OUTCOME_COLORS = {
  PASS: '#037f0c',
  FAIL: '#d91515',
  OTHER: '#e0a800',
} as const;

function barColor(task: GanttTask): string {
  if (task.outcome != null) {
    const outcome = task.outcome.toUpperCase();
    if (outcome === 'PASS') return OUTCOME_COLORS.PASS;
    if (outcome === 'FAIL') return OUTCOME_COLORS.FAIL;
    return OUTCOME_COLORS.OTHER;
  }
  return STAGE_COLORS[task.stage];
}

const LABEL_COLUMN_WIDTH = 260;
const ROW_HEIGHT = 24;
const BAR_HEIGHT = 14;

function taskTooltip(task: GanttTask): string {
  const total = formatDuration(task.endMs - task.startMs);
  const lines = [task.label, `Stage: ${STAGE_LABELS[task.stage]}`, `Total: ${total}`];
  if (task.outcome) lines.push(`Outcome: ${task.outcome}`);
  if (task.execStartMs != null) {
    lines.push(`Execution: ${formatDuration(task.endMs - task.execStartMs)}`);
    lines.push(`Queued: ${formatDuration(task.execStartMs - task.startMs)}`);
  }
  if (task.status) lines.push(`Status: ${task.status}`);
  lines.push(`Started: ${new Date(task.startMs).toLocaleTimeString()}`);
  return lines.join('\n');
}

export interface AuditTrailGanttProps {
  entries: AuditEntry[];
}

function LegendSwatch({ color, label }: { color: string; label: string }): React.JSX.Element {
  return (
    <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
      <span
        style={{
          display: 'inline-block',
          width: 10,
          height: 10,
          borderRadius: 2,
          backgroundColor: color,
        }}
      />
      <Box fontSize="body-s" color="text-body-secondary">
        {label}
      </Box>
    </SpaceBetween>
  );
}

export function AuditTrailGantt({ entries }: AuditTrailGanttProps) {
  const model = useMemo(() => buildGanttModel(entries), [entries]);
  const [ruleEvalsExpanded, setRuleEvalsExpanded] = useState(false);
  if (!model) return null;

  const ruleEvalSummary = summarizeRuleEvals(model.tasks);
  const stagesPresent = STAGE_ORDER.filter((stage) =>
    model.tasks.some((t) => t.stage === stage && !isRuleEvalTask(t)),
  );

  const renderBar = (
    startMs: number,
    endMs: number,
    execStartMs: number | null,
    color: string,
    tooltip: string,
  ) => {
    const leftPct = toPercent(model, startMs);
    const widthPct = Math.max(toPercent(model, endMs) - leftPct, 0.3);
    const execLeftPct = execStartMs != null ? toPercent(model, execStartMs) : null;
    return (
      <div style={{ position: 'relative', flex: 1, height: BAR_HEIGHT }}>
        <div
          title={tooltip}
          style={{
            position: 'absolute',
            left: `${leftPct}%`,
            width: `${widthPct}%`,
            height: BAR_HEIGHT,
            borderRadius: 3,
            backgroundColor: color,
            opacity: execLeftPct != null ? 0.35 : 0.9,
          }}
        />
        {execLeftPct != null && (
          <div
            title={tooltip}
            style={{
              position: 'absolute',
              left: `${execLeftPct}%`,
              width: `${Math.max(toPercent(model, endMs) - execLeftPct, 0.3)}%`,
              height: BAR_HEIGHT,
              borderRadius: 3,
              backgroundColor: color,
            }}
          />
        )}
      </div>
    );
  };


  const hasRuleTasks = model.tasks.some(isRuleEvalTask);

  // Expanded rule rows drop the repeated "Rule evaluation: " prefix (the
  // group row already says it) and let long rule names overflow into the
  // chart area instead of truncating; other rows keep ellipsis clipping.
  const RULE_LABEL_PREFIX = 'Rule evaluation: ';
  const renderTaskRow = (task: GanttTask, indent = false) => (
    <div key={task.entryIndex} style={{ display: 'flex', alignItems: 'center', height: ROW_HEIGHT }}>
      <div
        title={task.label}
        style={{
          width: LABEL_COLUMN_WIDTH,
          flexShrink: 0,
          paddingLeft: indent ? 16 : 0,
          paddingRight: 8,
          fontSize: 12,
          whiteSpace: 'nowrap',
          ...(indent
            ? { overflow: 'visible', zIndex: 1 }
            : { overflow: 'hidden', textOverflow: 'ellipsis' }),
        }}
      >
        {indent && task.label.startsWith(RULE_LABEL_PREFIX)
          ? task.label.slice(RULE_LABEL_PREFIX.length)
          : task.label}
      </div>
      {renderBar(task.startMs, task.endMs, task.execStartMs, barColor(task), taskTooltip(task))}
    </div>
  );

  // Assemble display rows: individual rule executions collapse into a single
  // summary row (inserted where the first one starts) that expands on click.
  const rows: React.ReactNode[] = [];
  let groupEmitted = false;
  for (const task of model.tasks) {
    if (ruleEvalSummary && isRuleEvalTask(task)) {
      if (!groupEmitted) {
        groupEmitted = true;
        const { startMs, endMs, total, passed, failed, other } = ruleEvalSummary;
        const counts = [
          `${passed} pass`,
          failed > 0 ? `${failed} fail` : null,
          other > 0 ? `${other} other` : null,
        ]
          .filter(Boolean)
          .join(' / ');
        const groupColor =
          failed > 0 ? OUTCOME_COLORS.FAIL : other > 0 ? OUTCOME_COLORS.OTHER : OUTCOME_COLORS.PASS;
        rows.push(
          <div
            key="rule-eval-group"
            role="button"
            tabIndex={0}
            aria-expanded={ruleEvalsExpanded}
            onClick={() => setRuleEvalsExpanded((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setRuleEvalsExpanded((v) => !v);
              }
            }}
            style={{ display: 'flex', alignItems: 'center', height: ROW_HEIGHT, cursor: 'pointer' }}
          >
            <div
              style={{
                width: LABEL_COLUMN_WIDTH,
                flexShrink: 0,
                paddingRight: 8,
                fontSize: 12,
                fontWeight: 700,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {ruleEvalsExpanded ? '\u25be' : '\u25b8'} Rule evaluations ({total}) {'\u2014'} {counts}
            </div>
            {renderBar(
              startMs,
              endMs,
              null,
              groupColor,
              `${total} rule evaluations\nSpan: ${formatTickLabel(endMs - startMs)}\n${counts}\nClick to ${ruleEvalsExpanded ? 'collapse' : 'expand'}`,
            )}
          </div>,
        );
        if (ruleEvalsExpanded) {
          for (const ruleTask of model.tasks.filter(isRuleEvalTask)) {
            rows.push(renderTaskRow(ruleTask, true));
          }
        }
      }
      continue;
    }
    rows.push(renderTaskRow(task));
  }

  return (
    <SpaceBetween size="xs">
      {/* Legend: one row per group, label column aligned */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          rowGap: 6,
          padding: '10px 14px',
          border: '1px solid var(--color-border-divider-default)',
          borderRadius: 8,
          width: 'fit-content',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ width: LEGEND_LABEL_WIDTH, flexShrink: 0 }}>
            <Box fontSize="body-s" fontWeight="bold">Stages</Box>
          </div>
          <SpaceBetween direction="horizontal" size="s" alignItems="center">
            {stagesPresent.map((stage) => (
              <LegendSwatch key={stage} color={STAGE_COLORS[stage]} label={STAGE_LABELS[stage]} />
            ))}
          </SpaceBetween>
        </div>
        {hasRuleTasks && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ width: LEGEND_LABEL_WIDTH, flexShrink: 0 }}>
              <Box fontSize="body-s" fontWeight="bold">Rule outcomes</Box>
            </div>
            <SpaceBetween direction="horizontal" size="s" alignItems="center">
              <LegendSwatch color={OUTCOME_COLORS.PASS} label="pass" />
              <LegendSwatch color={OUTCOME_COLORS.FAIL} label="fail" />
              <LegendSwatch color={OUTCOME_COLORS.OTHER} label="other" />
            </SpaceBetween>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ width: LEGEND_LABEL_WIDTH, flexShrink: 0 }}>
            <Box fontSize="body-s" fontWeight="bold">Bar fill</Box>
          </div>
          <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
            <span style={{ display: 'inline-flex' }}>
              <span
                style={{
                  display: 'inline-block',
                  width: 14,
                  height: 10,
                  borderRadius: '2px 0 0 2px',
                  backgroundColor: '#5f6b7a',
                  opacity: 0.35,
                }}
              />
              <span
                style={{
                  display: 'inline-block',
                  width: 14,
                  height: 10,
                  borderRadius: '0 2px 2px 0',
                  backgroundColor: '#5f6b7a',
                }}
              />
            </span>
            <Box fontSize="body-s" color="text-body-secondary">
              queued, then executing
            </Box>
          </SpaceBetween>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: 640 }}>
          {/* Axis */}
          <div style={{ display: 'flex' }}>
            <div style={{ width: LABEL_COLUMN_WIDTH, flexShrink: 0 }} />
            <div style={{ position: 'relative', flex: 1, height: 20 }}>
              {model.ticks.map((tick) => (
                <span
                  key={tick.ms}
                  style={{
                    position: 'absolute',
                    left: `${(tick.ms / model.totalMs) * 100}%`,
                    transform: 'translateX(-50%)',
                    fontSize: 11,
                    color: '#5f6b7a',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tick.label}
                </span>
              ))}
            </div>
          </div>

          {/* Rows */}
          <div style={{ position: 'relative' }}>
            {/* Gridlines spanning all rows */}
            <div
              style={{
                position: 'absolute',
                left: LABEL_COLUMN_WIDTH,
                right: 0,
                top: 0,
                bottom: 0,
                pointerEvents: 'none',
              }}
            >
              {model.ticks.map((tick) => (
                <div
                  key={tick.ms}
                  style={{
                    position: 'absolute',
                    left: `${(tick.ms / model.totalMs) * 100}%`,
                    top: 0,
                    bottom: 0,
                    borderLeft: '1px solid rgba(95, 107, 122, 0.15)',
                  }}
                />
              ))}
            </div>

            {rows}
          </div>

          {/* Footer: total span */}
          <div style={{ display: 'flex' }}>
            <div style={{ width: LABEL_COLUMN_WIDTH, flexShrink: 0 }} />
            <Box fontSize="body-s" color="text-body-secondary">
              Total span: {formatTickLabel(model.totalMs)}
            </Box>
          </div>
        </div>
      </div>
    </SpaceBetween>
  );
}
